import { useState, useRef } from "react";
import { Reorder } from "motion/react";
import { cmsUpdateSection, cmsDeleteSection, cmsReorderItems } from "api";
import ItemRow from "./ItemRow";
import ItemEditForm, { DeleteButton, ModalPortal } from "./ItemEditForm";
import { toast } from "sonner";
import { titleCase } from "./textUtils";

/* Detail panel for the section selected in the sidebar. */
function SectionCard({ section, handlers, existingModifiers, onEditItem }) {
  const [isEditingSection, setIsEditingSection] = useState(false);
  const [sectionHeader, setSectionHeader] = useState(section.header);
  const [sectionDesc, setSectionDesc] = useState(section.description || "");
  const [isAddingItem, setIsAddingItem] = useState(false);
  // ─── Drag & drop reordering (motion Reorder) ──────────────────────────────
  // Order updates live in state while dragging; the pre-drag order is kept
  // so a failed save can revert. Persisted once, on drag end.
  const preDragOrder = useRef(null);

  function handleReorder(ids) {
    if (!preDragOrder.current)
      preDragOrder.current = section.items.map((i) => i._id);
    handlers.reorderItems(section._id, ids);
  }

  async function handleDragEnd() {
    const prev = preDragOrder.current;
    preDragOrder.current = null;
    const ids = section.items.map((i) => i._id);
    if (!prev || prev.join() === ids.join()) return;
    try {
      await cmsReorderItems(section._id, ids);
    } catch (err) {
      console.error(err);
      handlers.reorderItems(section._id, prev);
      toast.error("Failed to save the new order. Reverted.");
    }
  }

  async function handleSaveSection() {
    if (!sectionHeader.trim()) return;
    const original = { ...section };
    handlers.updateSection({
      ...section,
      header: sectionHeader,
      description: sectionDesc,
      updatedAt: new Date().toISOString(),
    });
    setIsEditingSection(false);
    try {
      await cmsUpdateSection(section._id, {
        header: sectionHeader,
        description: sectionDesc,
      });
    } catch (err) {
      console.error(err);
      handlers.updateSection(original);
      setSectionHeader(original.header);
      setSectionDesc(original.description || "");
      toast.error("Failed to save section. Changes reverted.");
    }
  }

  function handleCancelSectionEdit() {
    setSectionHeader(section.header);
    setSectionDesc(section.description || "");
    setIsEditingSection(false);
  }

  // Confirmation is handled inline by the double-click DeleteButton.
  async function handleDeleteSection() {
    const backup = { ...section };
    await handlers.beforeCmsDelete?.();
    handlers.deleteSection(section._id);
    try {
      await cmsDeleteSection(section._id);
    } catch (err) {
      console.error(err);
      handlers.addSection(backup);
      toast.error("Failed to delete section. It has been restored.");
      return;
    }
    await handlers.afterCmsDelete?.();
  }

  function handleExpandItem(itemId) {
    onEditItem(itemId);
    setIsAddingItem(false);
  }

  function handleSaveNewItem(newItem) {
    handlers.addItem(newItem);
    setIsAddingItem(false);
  }

  return (
    <div className="cms-detail">
      <header className="cms-detail-head">
        <div className="cms-detail-title">
          <h3>{titleCase(section.header)}</h3>
          {section.description && <p>{section.description}</p>}
        </div>
        <div className="cms-detail-actions">
          <button
            className="cms-ghost-btn"
            onClick={() => {
              setSectionHeader(section.header);
              setSectionDesc(section.description || "");
              setIsEditingSection(true);
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit
          </button>
          <button
            className="cms-ghost-btn"
            onClick={() => {
              setIsAddingItem(true);
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add item
          </button>
        </div>
      </header>

      {section.items.length === 0 ? (
        <div className="cms-detail-empty">
          <p>No items in this section yet.</p>
          <p className="cms-detail-empty-sub">
            Use “Add item” to create the first one.
          </p>
        </div>
      ) : (
        <Reorder.Group
          as="div"
          axis="y"
          values={section.items.map((i) => i._id)}
          onReorder={handleReorder}
          className="cms-table"
          role="table"
          aria-label={section.header}
        >
          <div className="cms-table-head" role="row">
            <span role="columnheader">Item</span>
            <span role="columnheader">Description</span>
            <span className="cms-table-head-price" role="columnheader">
              Price
            </span>
            <span aria-hidden="true" />
          </div>
          {section.items.map((item) => (
            <ItemRow
              key={item._id}
              item={item}
              onToggle={() => handleExpandItem(item._id)}
              onDragEnd={handleDragEnd}
            />
          ))}
        </Reorder.Group>
      )}

      {isAddingItem && (
        <ItemEditForm
          sectionId={section._id}
          onSave={handleSaveNewItem}
          onCancel={() => setIsAddingItem(false)}
          existingModifiers={existingModifiers}
        />
      )}

      {isEditingSection && (
        <ModalPortal>
        <div
          className="cms-export-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCancelSectionEdit();
          }}
        >
          <div
            className="cms-export-modal cms-section-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Edit ${section.header}`}
          >
            <header className="cms-export-head">
              <h3>Edit Section</h3>
              <p>{titleCase(section.header)}</p>
            </header>
            <div className="cms-modal-body">
              <div className="cms-form-group">
                <label className="cms-form-label">Section Name</label>
                <input
                  className="cms-input"
                  value={sectionHeader}
                  onChange={(e) => setSectionHeader(e.target.value)}
                  placeholder="e.g. Pasta"
                  autoFocus
                />
              </div>
              <div className="cms-form-group">
                <label className="cms-form-label">Description</label>
                <input
                  className="cms-input"
                  value={sectionDesc}
                  onChange={(e) => setSectionDesc(e.target.value)}
                  placeholder="Optional description…"
                />
              </div>
            </div>
            <footer className="cms-export-foot">
              <DeleteButton
                label="Delete Section"
                onDelete={() => {
                  setIsEditingSection(false);
                  handleDeleteSection();
                }}
              />
              <div className="cms-export-foot-actions">
                <button
                  className="cms-btn-cancel"
                  onClick={handleCancelSectionEdit}
                >
                  Cancel
                </button>
                <button
                  className="cms-btn-save cms-export-go"
                  onClick={handleSaveSection}
                  disabled={!sectionHeader.trim()}
                >
                  Save Changes
                </button>
              </div>
            </footer>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}

export default SectionCard;
